
import { Link } from 'react-router-dom';
import { mockDiscussions } from '../../../mocks/discussions';
import { createDiscussionUrl } from '../../../hooks/useNavigation';

const FeaturedDiscussions = () => {
  // Pick 6 featured discussions from actual mock data
  const featuredIds = ['disc-001', 'disc-002', 'disc-003', 'gen-011', 'disc-005', 'disc-006'];
  const featuredDiscussions = featuredIds
    .map(id => mockDiscussions.find(d => d.id === id))
    .filter(Boolean)
    .map(d => ({
      id: d!.id,
      slug: d!.slug,
      category: d!.category,
      title: d!.title,
      author: d!.author.username,
      authorAvatar: d!.author.avatar,
      categoryLabel: d!.categoryName,
      categoryColor: d!.category === 'general' ? 'bg-amber-100 text-amber-800'
        : d!.category === 'reviews' ? 'bg-emerald-100 text-emerald-800'
        : d!.category === 'creators' ? 'bg-rose-100 text-rose-800'
        : d!.category === 'recommendations' ? 'bg-teal-100 text-teal-800'
        : 'bg-indigo-100 text-indigo-800',
      replies: d!.commentCount,
      likes: d!.upvotes,
      views: d!.views,
      timeAgo: (() => {
        const diff = Date.now() - new Date(d!.createdAt).getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours < 1) return 'Just now';
        if (hours < 24) return `${hours} hours ago`;
        return `${Math.floor(hours / 24)} day${Math.floor(hours / 24) > 1 ? 's' : ''} ago`;
      })(),
      excerpt: d!.content,
      isPinned: d!.isPinned,
      isEditorPick: d!.upvotes > 50,
      coverImage: `https://public.readdy.ai/ai/img_res/218c96ad33ad2ff572e402567be87a92.jpg${d!.id}&orientation=landscape`
    }));

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-sm border border-purple-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
            <i className="ri-star-smile-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Featured Discussions</h2>
            <p className="text-sm text-gray-500">Handpicked conversations by our community</p>
          </div>
        </div>
        <Link 
          to="/forum" 
          className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center whitespace-nowrap"
        >
          See All
          <i className="ri-arrow-right-line ml-1"></i>
        </Link>
      </div>
      
      <div className="space-y-4">
        {featuredDiscussions.map((discussion, index) => (
          <Link
            key={discussion.id}
            to={createDiscussionUrl(discussion.category, discussion.id, discussion.title)}
            className="block group"
          >
            <div className={`bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ${index === 0 ? 'ring-2 ring-purple-200' : ''}`}>
              {/* Cover Image for first item */}
              {index === 0 && (
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={discussion.coverImage}
                    alt={discussion.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 flex items-center space-x-2">
                    {discussion.isPinned && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white/90 text-purple-700">
                        <i className="ri-pushpin-fill mr-1"></i>
                        Pinned
                      </span>
                    )}
                    {discussion.isEditorPick && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-400 to-orange-400 text-white">
                        <i className="ri-award-fill mr-1"></i>
                        Editor&apos;s Pick
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="p-4">
                <div className="flex items-start space-x-3">
                  {/* Avatar - only show for non-first items */}
                  {index !== 0 && (
                    <img
                      src={discussion.authorAvatar}
                      alt={discussion.author}
                      className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0 ring-2 ring-purple-100"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    {/* Badges for non-first items */}
                    {index !== 0 && (
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        {discussion.isPinned && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            <i className="ri-pushpin-fill mr-1"></i>
                            Pinned
                          </span>
                        )}
                        {discussion.isEditorPick && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700">
                            <i className="ri-award-fill mr-1"></i>
                            Editor&apos;s Pick
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${discussion.categoryColor}`}>
                          {discussion.categoryLabel}
                        </span>
                      </div>
                    )}
                    
                    {/* Category badge for first item */}
                    {index === 0 && (
                      <div className="mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${discussion.categoryColor}`}>
                          {discussion.categoryLabel}
                        </span>
                      </div>
                    )}
                    
                    <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors line-clamp-2 mb-2">
                      {discussion.title}
                    </h3>
                    
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {discussion.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {index === 0 && (
                          <img
                            src={discussion.authorAvatar}
                            alt={discussion.author}
                            className="w-6 h-6 rounded-full object-cover object-top"
                          />
                        )}
                        <span className="text-sm font-medium text-gray-700">{discussion.author}</span>
                        <span className="text-xs text-gray-400">&bull;</span>
                        <span className="text-xs text-gray-500">{discussion.timeAgo}</span>
                      </div>
                      
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        <span className="flex items-center">
                          <i className="ri-eye-line mr-1"></i>
                          {discussion.views.toLocaleString()}
                        </span>
                        <span className="flex items-center">
                          <i className="ri-message-3-line mr-1"></i>
                          {discussion.replies}
                        </span>
                        <span className="flex items-center text-rose-500">
                          <i className="ri-heart-fill mr-1"></i>
                          {discussion.likes}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default FeaturedDiscussions;
